/**
 * Onboarding API Service
 * Handles all API calls related to the business onboarding workflow
 * Integrates with Story 14-5 backend endpoints
 */

import axios from 'axios';
import { OnboardingFormData } from '../store/onboardingStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

// ============================================
// TYPES
// ============================================

export interface OnboardingSubmissionData {
  formData: OnboardingFormData;
  documentIds: string[];
}

export interface OnboardingApplicationResponse {
  applicationId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  nextSteps?: string[];
  estimatedReviewTime?: string;
}

export interface DocumentUploadResponse {
  documentId: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  status: 'PENDING' | 'VERIFIED' | 'REJECTED';
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface OnboardingValidationResponse {
  isValid: boolean;
  errors?: ValidationError[];
}

// ============================================
// API SERVICE CLASS
// ============================================

class OnboardingApiService {
  /**
   * Upload a document for the onboarding application
   * @param file - File to upload
   * @param documentType - Type of document (business_license, tax_document, etc.)
   * @param onProgress - Callback for upload progress
   */
  async uploadDocument(
    file: File,
    documentType: string,
    onProgress?: (progress: number) => void
  ): Promise<DocumentUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);

      // For now, simulate upload with progress tracking
      // In production, replace with actual API call using axios with onUploadProgress
      return await this.simulateUpload(file, documentType, onProgress);
    } catch (error) {
      throw this.handleError(error, 'Document upload');
    }
  }

  /**
   * Simulate file upload with progress tracking
   * TODO: Replace with actual API call in production
   */
  private async simulateUpload(
    file: File,
    documentType: string,
    onProgress?: (progress: number) => void
  ): Promise<DocumentUploadResponse> {
    return new Promise((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 30;
        if (progress > 100) progress = 100;

        if (onProgress) {
          onProgress(Math.round(progress));
        }

        if (progress >= 100) {
          clearInterval(interval);
          resolve({
            documentId: `doc-${Date.now()}`,
            fileName: file.name,
            fileSize: file.size,
            uploadedAt: new Date().toISOString(),
            status: 'PENDING',
          });
        }
      }, 200);
    });
  }

  /**
   * Validate a single step of the onboarding form
   * @param step - Step number (1-5)
   * @param formData - Form data for that step
   */
  async validateStep(
    step: number,
    formData: Partial<OnboardingFormData>
  ): Promise<OnboardingValidationResponse> {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post<any>(
        `${API_BASE_URL}/onboarding/validate/step-${step}`,
        { formData },
        { headers }
      );

      if (response.data.success) {
        return response.data.data || { isValid: true };
      }

      return {
        isValid: false,
        errors: response.data.data?.errors || [],
      };
    } catch (error) {
      throw this.handleError(error, 'Step validation');
    }
  }

  /**
   * Convert File or Blob to base64 string
   */
  private async fileToBase64(file: File | Blob | any): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        // Check if it's already a data URL string
        if (typeof file === 'string' && file.startsWith('data:')) {
          resolve(file);
          return;
        }

        // Check if it's a File or Blob
        if (!(file instanceof Blob) && !(file instanceof File)) {
          reject(new Error('Invalid file type: must be File or Blob'));
          return;
        }

        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          if (typeof result === 'string') {
            resolve(result);
          } else {
            reject(new Error('Failed to convert file to base64'));
          }
        };
        reader.onerror = () => {
          reject(new Error('FileReader error: ' + reader.error?.message));
        };
        reader.readAsDataURL(file);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Submit the complete onboarding application
   * @param formData - Complete form data
   * @param uploadedFiles - Array of uploaded file objects with actual File data
   */
  async submitApplication(
    formData: OnboardingFormData,
    uploadedFiles: any[]
  ): Promise<OnboardingApplicationResponse> {
    try {
      // Convert files to base64
      const documentBase64Map: Record<string, { base64: string; filename: string }> = {};

      for (const fileObj of uploadedFiles) {
        if (fileObj && fileObj.fieldName) {
          // Check if originalFile exists and is valid
          const fileToConvert = fileObj.originalFile;
          if (fileToConvert) {
            try {
              const base64 = await this.fileToBase64(fileToConvert);
              documentBase64Map[fileObj.fieldName] = {
                base64,
                filename: fileObj.name || 'document',
              };
            } catch (conversionError) {
              console.error(`Failed to convert file ${fileObj.fieldName}:`, conversionError);
              // Continue with other files
            }
          }
        }
      }

      // Prepare JSON payload with base64 documents
      const payload = {
        // Step 1: Business Information
        store_name: formData.businessName,
        llc_name: formData.llcName,
        address_line1: formData.addressLine1,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip,

        // Step 2: Contact Information
        full_name: formData.contactName,
        email: formData.contactEmail,
        contact_phone: formData.contactPhone,

        // Step 3: Documents as base64
        tax_id_document_base64: documentBase64Map['tax_id_document']?.base64 || null,
        tax_id_document_filename: documentBase64Map['tax_id_document']?.filename || null,
        federal_id_document_base64: documentBase64Map['federal_id_document']?.base64 || null,
        federal_id_document_filename: documentBase64Map['federal_id_document']?.filename || null,
        tobacco_license_document_base64: documentBase64Map['tobacco_license_document']?.base64 || null,
        tobacco_license_document_filename: documentBase64Map['tobacco_license_document']?.filename || null,

        // Step 4: Banking Information
        bank_name: formData.bankName,
        account_type: formData.accountType,
        account_number: formData.accountNumber,
        routing_number: formData.routingNumber,

        // Step 5: Agreement & Signature
        signature_data_url: formData.signatureData,
        terms_accepted_at: new Date().toISOString(),
      };

      const token = localStorage.getItem('accessToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post<any>(
        `${API_BASE_URL}/onboarding/applications`,
        payload,
        { headers }
      );

      if (response.status === 201 && response.data) {
        return {
          applicationId: response.data.id,
          status: response.data.status as 'PENDING' | 'APPROVED' | 'REJECTED',
          createdAt: response.data.submitted_at,
        };
      }

      throw new Error(response.data.message || 'Application submission failed');
    } catch (error) {
      throw this.handleError(error, 'Application submission');
    }
  }

  /**
   * Fetch onboarding application status
   * @param applicationId - Application ID
   */
  async getApplicationStatus(applicationId: string): Promise<OnboardingApplicationResponse> {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get<any>(
        `${API_BASE_URL}/onboarding/applications/${applicationId}`,
        { headers }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to fetch application status');
    } catch (error) {
      throw this.handleError(error, 'Get application status');
    }
  }

  /**
   * Check if email is already registered
   * @param email - Email to check
   */
  async checkEmailExists(email: string): Promise<boolean> {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post<any>(
        `${API_BASE_URL}/onboarding/check-email`,
        { email },
        { headers }
      );

      return response.data.data?.exists || false;
    } catch (error) {
      // Treat 404 as email doesn't exist
      if ((error as any).response?.status === 404) {
        return false;
      }
      throw this.handleError(error, 'Email check');
    }
  }

  /**
   * Get list of uploaded documents for current application
   */
  async getUploadedDocuments(): Promise<DocumentUploadResponse[]> {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get<any>(
        `${API_BASE_URL}/onboarding/documents`,
        { headers }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      return [];
    } catch (error) {
      throw this.handleError(error, 'Get uploaded documents');
    }
  }

  /**
   * Delete an uploaded document
   * @param documentId - Document ID to delete
   */
  async deleteDocument(documentId: string): Promise<void> {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.delete<any>(
        `${API_BASE_URL}/onboarding/documents/${documentId}`,
        { headers }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete document');
      }
    } catch (error) {
      throw this.handleError(error, 'Delete document');
    }
  }

  /**
   * Handle API errors with user-friendly messages
   */
  private handleError(error: any, context: string): Error {
    console.error(`[OnboardingAPI] ${context} failed:`, error);

    if (error?.response?.status === 401) {
      return new Error('Your session has expired. Please log in again.');
    }

    if (error?.response?.status === 422) {
      const validationErrors = error.response.data?.data?.errors;
      if (validationErrors && Array.isArray(validationErrors)) {
        const firstError = validationErrors[0];
        return new Error(firstError.message || 'Validation failed');
      }
      return new Error('Please check your input and try again');
    }

    if (error?.response?.status === 400) {
      const errorMessage = error.response.data?.message || 'Invalid request';
      // Check for rate limiting / too many submissions error
      if (errorMessage.toLowerCase().includes('too many')) {
        return new Error('Too many applications submitted. Please wait a few minutes before submitting another application.');
      }
      return new Error(errorMessage);
    }

    if (error?.response?.status === 429) {
      // HTTP 429 Too Many Requests
      return new Error('Too many requests. Please wait a few minutes before trying again.');
    }

    if (error?.response?.status === 413) {
      return new Error('File is too large. Maximum size is 10MB');
    }

    if (error?.response?.status === 500) {
      return new Error('Server error. Please try again later');
    }

    if (error?.message) {
      return error;
    }

    return new Error(`${context} failed. Please try again.`);
  }
}

// Export singleton instance
export const onboardingApi = new OnboardingApiService();
export default onboardingApi;
